<template>
  <div class="meeting-modal-wrapper">
    <v-dialog
      v-model="$store.meetingStore.state.currentMeeting"
      :max-width="$vuetify.breakpoint.mobile ? '100%' : 1400"
      :fullscreen="$vuetify.breakpoint.mobile"
      scrollable
      persistent
      :retain-focus="false"
      content-class="meeting-dialog"
    >
      <!-- Loading state -->
      <v-card v-if="$store.meetingStore.state.currentMeeting.loading" class="modal-card loading-card">
        <div class="loading-header">
          <div class="modal-brand">
            <span class="brand-icon-dark"><v-icon small color="#2D4B95">mdi-calendar-account-outline</v-icon></span>
            <span class="brand-label-dark">Cargando reunión</span>
          </div>
          <v-btn class="topbar-action-btn big" dark small color="#F4516C" @click="closeModal">
            <v-icon dark small>mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="loading-body">
          <v-progress-circular :size="40" :width="3" color="#3B5FDB" indeterminate />
          <p class="loading-text">Cargando información…</p>
        </div>
      </v-card>

      <!-- Main card -->
      <v-card v-else class="modal-card">

        <ModalStickyHeader>
        <!-- ── TOP BAR ── -->
        <template #topbar>
        <div class="topbar">
          <div class="topbar-left">
            <span class="topbar-icon"><v-icon small color="#2D4B95">mdi-calendar-account-outline</v-icon></span>
            <div class="topbar-titles">
              <p class="mb-0 caption">Reunión/Minuta <span class="caption font-weight-bold topbar-number">Nº{{ currentMeeting.number }}</span></p>
              <h2 class="topbar-title" v-if="!editMode">{{ currentMeeting.title }}</h2>
              <v-text-field
                v-else
                v-model="editForm.title"
                outlined
                dense
                hide-details
                class="title-edit-input"
                placeholder="Título de la reunión"
              />
            </div>
          </div>
          <div class="topbar-right">
            <button v-if="!editMode && canManageMeeting" class="text-action-btn" @click="startEdit">
              <v-icon size="14" color="#2D4B95">mdi-pencil-outline</v-icon>
              Editar
            </button>
            <template v-else-if="editMode">
              <button class="text-action-btn" @click="cancelEdit">Cancelar</button>
              <button class="create-btn" :disabled="savingEdit" @click="saveEdit">
                <v-progress-circular v-if="savingEdit" :size="13" :width="2" indeterminate color="white" />
                <v-icon v-else size="14" color="white">mdi-check</v-icon>
                Guardar
              </button>
            </template>
						<v-btn class="topbar-action-btn big" dark small color="#F4516C" @click="closeModal">
							<v-icon dark small>mdi-close</v-icon>
						</v-btn>
          </div>
        </div>
        </template>

        <!-- ── STATUS STRIP ── -->
        <template #status-strip>
        <div class="status-strip">
          <div class="status-strip-pills">
            <span class="pill pill--blue">
              <v-icon size="12" color="#2D4B95">mdi-calendar-month-outline</v-icon>
              {{ currentMeeting.created_at | toDateTime }}
            </span>
            <span class="pill pill--indigo" v-if="currentMeeting.start">
              <v-icon size="12" color="#4338CA">mdi-calendar-start-outline</v-icon>
              Inicio: {{ currentMeeting.start | toDateTime }}
            </span>
            <span class="pill pill--indigo" v-if="currentMeeting.end">
              <v-icon size="12" color="#4338CA">mdi-calendar-end-outline</v-icon>
              Término: {{ currentMeeting.end | toDateTime }}
            </span>
            <span class="pill" :class="'pill--modality-' + modality">
              <v-icon size="12">{{ modality === 'presencial' ? 'mdi-map-marker-outline' : 'mdi-video-outline' }}</v-icon>
              {{ modality === 'presencial' ? (currentMeeting.meeting_info && currentMeeting.meeting_info.address) || 'Presencial' : 'Virtual' }}
            </span>
            <span class="pill" :class="'pill--status-' + currentMeeting.status">
              <v-icon size="12">mdi-progress-check</v-icon>
              {{ statusLabel(currentMeeting.status) }}
            </span>
            <div class="d-flex align-center" style="gap:5px">
              <v-divider vertical />
              <p class="mb-0 text-primary" style="font-size: 10px;">Visto por:</p>
              <SeenByUsers
                v-model="seenByMenu"
                :seenByUsers="currentMeeting.seen_by || []"
              />
            </div>
          </div>
          <div class="status-strip-actions">
            <div class="d-flex align-center" style="gap:10px" v-if="isLoading">
              <v-progress-circular :size="16" :width="2" color="#3B5FDB" indeterminate />
            </div>
            <v-menu v-else-if="canManageMeeting" offset-y>
              <template v-slot:activator="{ on, attrs }">
								<v-btn
									class="status-change-btn"
									small
									outlined
									v-bind="attrs"
									v-on="on"
								>
									<v-icon size="14" color="#2D4B95" start>mdi-swap-horizontal-bold</v-icon>
									<p class="mb-0 ml-1 caption text-none font-weight-bold">Cambiar estado</p>
								</v-btn>
              </template>
              <div class="create-menu">
                <button
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  class="create-menu-item"
                  :disabled="currentMeeting.status === opt.value"
                  @click="changeStatus(opt.value)"
                >
                  <v-icon size="14" :color="opt.color">{{ opt.icon }}</v-icon>
                  {{ opt.label }}
                </button>
              </div>
            </v-menu>
            <span v-if="currentMeeting.status === 3 && isUserPart && isUserSigned" class="signed-indicator">
              <v-icon size="14" color="#1B7A4E">mdi-check-decagram</v-icon>
              Ya has firmado
            </span>
						<v-btn
							class="status-change-btn"
							small
							outlined
							:loading="isLoading"
							:disabled="isLoading || signatureUploading || isSigning" 
							v-else-if="currentMeeting.status === 3 && isUserPart"
							@click="handleSignMeeting"
						>
							<v-icon size="14" color="#2D4B95" start>mdi-pen</v-icon>
							<p class="mb-0 ml-1 caption text-none font-weight-bold">Firmar</p>
						</v-btn>
						<v-btn
							v-if="canManageMeeting"
							class="status-change-btn"
							small
							outlined
							:loading="isLoading"
							:disabled="isLoading"
							@click="sendMeetingEmail"
						>
							<v-icon size="14" color="#2D4B95" start>mdi-email</v-icon>
							<p class="mb-0 ml-1 caption text-none font-weight-bold">Enviar A Participantes</p>
						</v-btn>
						<v-btn
							class="status-change-btn"
							small
							outlined
							:loading="isLoading"
							:disabled="isLoading"
							@click="exportPdf"
						>
							<v-icon size="14" color="#2D4B95" start>mdi-file-export-outline</v-icon>
							<p class="mb-0 ml-1 caption text-none font-weight-bold">Exportar PDF</p>
						</v-btn>
          </div>
        </div>
        </template>
        </ModalStickyHeader>

        <!-- ── TWO COLUMN BODY ── -->
        <div class="modal-body" :class="{ resizing: isResizing }">

          <!-- LEFT: detail panel -->
          <div class="panel-detail" :style="$vuetify.breakpoint.mobile ? {} : { width: leftPanelWidth + '%' }">
            <div class="panel-scroll">

              <!-- Meta grid -->
              <section class="meta-section">
                <div class="meta-two-columns">
                  <div class="meta-left-column">
                    <div class="meta-item">
                      <span class="meta-label">Creador</span>
                      <span class="meta-value">{{ currentMeeting.creator.name }}</span>
                    </div>
                    <div class="meta-item">
                      <span class="meta-label">Proyecto</span>
                      <span class="meta-value">{{ currentMeeting.project.name }}</span>
                    </div>
                  </div>
                  <div class="meta-right-column">
                    <div class="meta-item">
                      <span class="meta-label">Responsable</span>
                      <div class="meta-value meta-responsible">
                        <span>{{ currentMeeting.responsible ? currentMeeting.responsible.name : 'Sin asignar' }}</span>
                        <v-menu offset-y v-if="!editMode && canManageMeeting" @input="checkUsers(); searchUserResponsable = ''">
                          <template v-slot:activator="{ on, attrs }">
                            <button class="icon-action-btn" v-bind="attrs" v-on="on">
                              <v-icon size="14" color="#2D4B95">mdi-swap-horizontal-bold</v-icon>
                            </button>
                          </template>
                          <v-list dense class="py-0 responsible-menu">
                            <v-list-item dense style="min-height:32px; background:#2D4B95;">
                              <p class="caption mb-0 white--text font-weight-bold">Cambiar responsable</p>
                            </v-list-item>
                            <div class="search-responsible-wrapper" @click.stop>
                              <v-icon size="14" color="#999">mdi-magnify</v-icon>
                              <input
                                v-model="searchUserResponsable"
                                type="text"
                                placeholder="Buscar usuario…"
                                class="search-responsible-input"
                                @click.stop
                              />
                            </div>
                            <div v-if="$store.usersStore.state.loadingUserListForItems" class="pa-3 text-center">
                              <v-progress-linear indeterminate color="#2D4B95" />
                            </div>
                            <v-list-item-group v-else color="#2D4B95" class="overflow-y-auto" style="max-height:260px">
                              <v-list-item
                                v-for="(user, i) in $store.usersStore.state.usersListForItems.users.filter(u => {
                                  const search = searchUserResponsable.toLowerCase();
                                  return u.id !== currentMeeting.responsible_id &&
                                    (u.name.toLowerCase().includes(search) || u.email.toLowerCase().includes(search));
                                })"
                                :key="i" dense class="px-0 py-0" @click="changeResponsible(user.id)">
                                <div style="width:100%">
                                  <div class="d-flex align-center px-3 py-2 gap-3">
                                    <img :src="user.info.avatar" class="user-avatar-sm" />
                                    <div>
                                      <p class="mb-0 caption font-weight-bold" style="color:#333">{{ user.name }} — <span style="color:#2D4B95">{{ user.projectjob.userjobtype.name }}</span></p>
                                      <p class="mb-0 caption" style="color:#777">{{ user.email }}</p>
                                    </div>
                                  </div>
                                  <v-divider />
                                </div>
                              </v-list-item>
                            </v-list-item-group>
                          </v-list>
                        </v-menu>
                      </div>
                    </div>
                  </div>
                </div>

                <v-divider class="meta-divider" />

                <!-- Fecha / hora + Modalidad (solo lectura o edición) -->
                <div class="meta-two-columns" v-if="!editMode">
                  <div class="meta-left-column">
                    <div class="meta-item">
                      <span class="meta-label">Fecha inicio</span>
                      <span class="meta-value">{{ currentMeeting.start | toDateTime }}</span>
                    </div>
                    <div class="meta-item" v-if="currentMeeting.end">
                      <span class="meta-label">Fecha término</span>
                      <span class="meta-value">{{ currentMeeting.end | toDateTime }}</span>
                    </div>
                  </div>
                  <div class="meta-right-column">
                    <div class="meta-item">
                      <span class="meta-label">{{ modality === 'presencial' ? 'Dirección' : 'Link videollamada' }}</span>
                      <span class="meta-value" v-if="modality === 'presencial'">{{ (currentMeeting.meeting_info && currentMeeting.meeting_info.address) || 'Sin dirección' }}</span>
                      <a v-else class="meta-value meta-link" :href="currentMeeting.meeting_info && currentMeeting.meeting_info.link" target="_blank">{{ (currentMeeting.meeting_info && currentMeeting.meeting_info.link) || 'Sin link' }}</a>
                    </div>
                  </div>
                </div>

                <div v-else class="edit-block">
                  <div class="fields-grid four-col">
                    <div class="field-group">
                      <label class="field-label">Fecha inicio</label>
                      <v-menu v-model="startDateMenu" :close-on-content-click="false" max-width="290">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            :value="formatDatePart(startDatePart)"
                            placeholder="Seleccionar"
                            prepend-inner-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            hide-details
                            class="remove-bottom-details"
                          />
                        </template>
                        <v-date-picker locale="es" v-model="startDatePart" :min="todayDate" no-title />
                      </v-menu>
                    </div>
                    <div class="field-group">
                      <label class="field-label">Hora inicio</label>
                      <SimpleTimePicker v-model="startTimePart" :min-time="startMinTime" placeholder="Seleccionar" />
                    </div>
                    <div class="field-group">
                      <label class="field-label">Fecha término</label>
                      <v-menu v-model="endDateMenu" :close-on-content-click="false" max-width="290">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            :value="formatDatePart(endDatePart)"
                            placeholder="Sin fecha"
                            prepend-inner-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            dense
                            hide-details
                            class="remove-bottom-details"
                          />
                        </template>
                        <v-date-picker locale="es" v-model="endDatePart" :min="minEndDate" no-title />
                      </v-menu>
                    </div>
                    <div class="field-group">
                      <label class="field-label">Hora término</label>
                      <SimpleTimePicker v-model="endTimePart" :min-time="endMinTime" placeholder="Sin hora" :disabled="!endDatePart" />
                    </div>
                  </div>
                  <p v-if="endDateError" class="field-error">{{ endDateError }}</p>

                  <div class="modality-group mt-3">
                    <button class="modality-btn" :class="{ active: modality === 'presencial' }" type="button" @click="modality = 'presencial'">
                      <v-icon size="15" :color="modality === 'presencial' ? '#fff' : '#5E85EF'">mdi-map-marker-outline</v-icon>
                      Presencial
                    </button>
                    <button class="modality-btn" :class="{ active: modality === 'virtual' }" type="button" @click="modality = 'virtual'">
                      <v-icon size="15" :color="modality === 'virtual' ? '#fff' : '#5E85EF'">mdi-video-outline</v-icon>
                      Virtual
                    </button>
                  </div>
                  <div class="modality-field mt-2">
                    <v-text-field
                      v-if="modality === 'presencial'"
                      v-model="editForm.address"
                      placeholder="Dirección de la reunión"
                      prepend-inner-icon="mdi-map-marker-outline"
                      outlined dense hide-details
                    />
                    <v-text-field
                      v-else
                      v-model="editForm.link"
                      placeholder="Link de videollamada"
                      prepend-inner-icon="mdi-video-outline"
                      outlined dense hide-details
                    />
                  </div>
                </div>

                <v-divider class="meta-divider" />

                <!-- Participantes -->
                <div class="meta-item">
                  <div class="d-flex align-center justify-space-between">
                    <span class="meta-label">Participantes</span>
                    <v-menu v-if="canManageMeeting" v-model="addParticipantMenu" offset-y :close-on-content-click="false" @input="checkUsers()">
                      <template v-slot:activator="{ on, attrs }">
                        <button class="add-subtask-btn" v-bind="attrs" v-on="on">
                          <v-icon size="13" color="#6B7280">mdi-plus</v-icon>
                          Agregar
                        </button>
                      </template>
                      <v-card class="pa-2" style="width:260px">
                        <v-autocomplete
                          v-model="participantToAdd"
                          :items="availableParticipants"
                          :loading="$store.usersStore.state.loadingUserListForItems"
                          item-text="name"
                          item-value="id"
                          outlined
                          dense
                          hide-details
                          placeholder="Buscar usuario"
                          @change="addParticipant"
                        />
                      </v-card>
                    </v-menu>
                  </div>
                  <div v-if="meetingParticipants.length" class="participant-list mt-2">
                    <div v-for="p in meetingParticipants" :key="p.id" class="participant-card">
                      <div class="participant-left">
                        <div class="p-avatar">
                          <img v-if="p.info && p.info.avatar" :src="p.info.avatar" />
                          <span v-else>{{ initials(p.name) }}</span>
                        </div>
                        <div class="p-info">
													<span v-if="p.title" class="p-title">{{ p.title }}</span>
                          <span class="p-name">{{ p.name }}</span>
                        </div>
                      </div>
                      <div class="participant-right">
                        <template v-if="currentMeeting.status === 3">
                          <v-menu v-if="getParticipantSignature(p.id)" offset-y open-on-hover :close-on-content-click="true">
                            <template v-slot:activator="{ on, attrs }">
                              <span class="signature-chip signature-chip--signed" v-bind="attrs" v-on="on">
                                <v-icon size="12" color="#1B7A4E">mdi-check-circle</v-icon>
                                Firmado
                              </span>
                            </template>
                            <v-card class="pa-3 signature-preview-card">
                              <p class="caption mb-2 font-weight-bold" style="color:#374151">Firma de {{ p.name }}</p>
                              <img :src="getParticipantSignature(p.id).user.signature.url" class="signature-preview-img" />
                            </v-card>
                          </v-menu>
                          <span v-else class="signature-chip signature-chip--pending">
                            <v-icon size="12" color="#B45309">mdi-clock-outline</v-icon>
                            Sin firmar
                          </span>
                        </template>
                        <button v-if="canManageMeeting" class="p-remove-btn" type="button" @click="removeParticipant(p.id)">
                          <v-icon size="14" color="#9CA3AF">mdi-trash-can-outline</v-icon>
                        </button>
                      </div>
                    </div>
                  </div>
                  <p v-else class="empty-note">Sin participantes</p>
                </div>
              </section>

              <!-- Description -->
              <section class="content-section">
                <h3 class="section-title">Descripción</h3>
                <InteractionEditor
                  v-if="editMode"
                  v-model="editForm.description"
                  item_type="meeting"
                />
                <div v-else class="description-body" v-html="currentMeeting.description"></div>
              </section>

              <!-- Fotos -->
              <section class="content-section">
                <div class="section-header-row">
                  <h3 class="section-title">Fotos</h3>
                  <button v-if="canManageMeeting" class="text-action-btn" @click="onPhotoButtonClick">
                    <v-icon size="14" color="#2D4B95">mdi-image-plus-outline</v-icon>
                    Agregar foto
                  </button>
                  <input ref="photoUploader" class="d-none" type="file" accept="image/*" @change="onPhotoFileChanged" />
                </div>
                <div class="photo-gallery" v-if="photoAttachments.length > 0">
                  <div class="photo-card" v-for="photo in photoAttachments" :key="photo.id" :class="{ 'photo-card--main': isMainImage(photo) }">
                    <img :src="photo.pathmedia.path" class="photo-img" />
                    <div class="photo-overlay" v-if="canManageMeeting">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <button class="photo-btn" v-bind="attrs" v-on="on" :disabled="isMainImage(photo) || settingMainImage" @click="setMainImage(photo)">
                            <v-icon size="14" :color="isMainImage(photo) ? '#F59E0B' : 'white'">{{ isMainImage(photo) ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
                          </button>
                        </template>
                        <span>{{ isMainImage(photo) ? 'Imagen principal' : 'Marcar como principal' }}</span>
                      </v-tooltip>
                      <button class="photo-btn photo-btn--danger" @click="deletePhoto(photo)">
                        <v-icon size="14" color="white">mdi-trash-can-outline</v-icon>
                      </button>
                    </div>
                    <span v-if="isMainImage(photo)" class="photo-main-badge">
                      <v-icon size="11" color="white">mdi-star</v-icon> Principal
                    </span>
                  </div>
                </div>
                <p class="empty-note" v-else>Sin fotos</p>
              </section>

              <!-- Adjuntos -->
              <section class="content-section" v-if="fileAttachments.length > 0">
                <div class="section-header-row">
                  <h3 class="section-title">Adjuntos</h3>
                  <button class="text-action-btn"
                    v-if="fileAttachments.length > 1"
                    :disabled="loadingFilePath"
                    @click="downloadAttachments(fileAttachments)">
                    <v-icon size="14" color="#3B5FDB">mdi-download-multiple</v-icon>
                    Descargar todo
                  </button>
                </div>
                <div class="attachments-list">
                  <div class="attachment-row" v-for="(att, i) in fileAttachments" :key="i">
                    <div class="attachment-info">
                      <v-icon size="16" color="#3B5FDB">mdi-file-outline</v-icon>
                      <span class="attachment-name">{{ att.name }}</span>
                    </div>
                    <div class="attachment-actions">
                      <AttachmentPreviewButton
                        :attachment="normalizeAttachment(att)"
                        :loading="loadingFilePath"
                        compact
                        plain
                        @preview="previewAttachment"
                      />
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <button class="icon-action-btn" v-bind="attrs" v-on="on" :disabled="loadingFilePath" @click="getFilesUrl(att, true)">
                            <v-icon size="14" color="#3B5FDB">mdi-open-in-new</v-icon>
                          </button>
                        </template>
                        <span>Ver en navegador</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <button class="icon-action-btn" v-bind="attrs" v-on="on" :disabled="loadingFilePath" @click="getFilesUrl(att, true, true)">
                            <v-icon size="14" color="#3B5FDB">mdi-cloud-download-outline</v-icon>
                          </button>
                        </template>
                        <span>Descargar</span>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
              </section>

            </div>
          </div>

          <!-- Resizer (desktop only) -->
          <div class="panel-resizer" @mousedown="startResize" v-if="!$vuetify.breakpoint.mobile">
            <div class="resize-handle"></div>
          </div>

          <!-- RIGHT: interactions panel -->
          <div class="panel-interactions" :class="{ 'panel-interactions--editor-open': showEditor || editConsolidationMode }">
						<div class="pa-2">
							<div
								class="consolidation-card"
								v-show="currentMeeting.consolidation || isResponsible"
							>
								<div class="consolidation-card__header">
									<template v-if="currentMeeting.consolidation">
										<img
											v-if="currentMeeting.consolidation?.user?.info?.avatar"
											:src="currentMeeting.consolidation.user.info.avatar"
											class="consolidation-card__avatar"
											alt=""
										/>
										<div v-else class="consolidation-card__avatar consolidation-card__avatar--fallback">
											{{ currentMeeting.consolidation?.user?.name?.charAt(0) }}
										</div>

										<div class="consolidation-card__meta">
											<span class="consolidation-card__label">Minuta consolidada</span>
											<span class="consolidation-card__author">
												Última edición por <strong>{{ currentMeeting.consolidation?.user?.name }}</strong>
											</span>
										</div>
									</template>

									<div v-else class="consolidation-card__meta">
										<span class="consolidation-card__label">Minuta consolidada</span>
									</div>

									<div class="consolidation-card__actions">
										<button class="add-comment-btn" @click="toggleConsolidationEditor" :disabled="showEditor" v-if="isResponsible">
											<v-icon size="16" color="#2D4B95">{{ editConsolidationMode ? 'mdi-close' : 'mdi-comment-plus-outline' }}</v-icon>
											{{ editConsolidationMode ? 'Cancelar' : 'Editar Consolidado' }}
										</button>
										<p class="caption mb-0" style="color:#999" v-else>Espacio sólo editable por responsable</p>
									</div>
								</div>

								<div
									class="consolidation-card__content"
									v-show="!(isResponsible && editConsolidationMode) && currentMeeting.consolidation"
									v-html="currentMeeting.consolidation?.message"
								></div>

								<div class="consolidation-editor-container" v-if="isResponsible && editConsolidationMode">
									<InteractionEditor
										v-model="message"
										item_type="meeting"
										:item="currentMeeting"
										:editor_options="{ plugins: 'link table lists', resize: 'vertical', height: 400, max_height: 600 }"
										@paste-image="onPasteImage"
										@mentions-updated="onMentionsUpdated" />

									<div v-if="filesPreview.length > 0" class="attachments-preview">
										<button class="attach-toggle" @click="showAttachments = !showAttachments">
											<span class="attach-dot"></span>
											{{ filesPreview.length }} {{ filesPreview.length === 1 ? 'adjunto' : 'adjuntos' }}
											<v-icon size="14" :class="['chevron', { open: showAttachments }]">mdi-chevron-down</v-icon>
										</button>
										<div v-if="showAttachments" class="attach-grid">
											<div class="img-chip" v-for="(f, i) in filesPreview.filter(f => f.src)" :key="'img-'+i">
												<img :src="f.src" class="img-thumb" />
                        <span v-if="f.source === 'library'" class="attachment-reference-tag">Biblioteca</span>
												<button class="chip-remove" @click="removeAttachment(f, i)"><v-icon x-small color="white">mdi-close</v-icon></button>
											</div>
											<div class="file-chip" v-for="(f, i) in filesPreview.filter(f => !f.src)" :key="'file-'+i">
												<v-icon small color="#2D4B95">mdi-file-outline</v-icon>
												<span class="file-name">{{ f.name }}</span>
                        <span v-if="f.source === 'library'" class="attachment-reference-tag">Biblioteca</span>
												<button class="chip-remove file" @click="removeAttachment(f, i)"><v-icon x-small>mdi-close</v-icon></button>
											</div>
										</div>
									</div>

									<div class="compose-actions mt-1">
										<button class="send-btn" :disabled="loading" @click="preAddInteraction(true)">
											<v-progress-circular v-if="loading" :size="14" :width="2" indeterminate color="white" />
											<v-icon v-else size="15" color="white">mdi-send</v-icon>
											{{ confirmButtonText }}
										</button>
										<div class="compose-secondary">
                      <AttachmentSourceButton
                        @files-selected="onFilesChanged"
                        @library-file-selected="onLibraryFileSelected"
                      />
											<button class="text-action-btn" @click="toggleConsolidationEditor">
												<v-icon size="15" color="#666">mdi-close</v-icon>
												Cancelar
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>

						<v-divider v-if="isResponsible || true" class="mt-2 mb-2" />

            <div class="interactions-header">
              <div class="d-flex align-center" style="gap: 15px;">
                <h3 class="section-title" style="margin:0">Interacciones</h3>
                <div class="add-comment-section">
                  <button class="add-comment-btn" @click="toggleEditorWithScroll" :disabled="editConsolidationMode">
                    <v-icon size="16" color="#2D4B95">{{ showEditor ? 'mdi-close' : 'mdi-comment-plus-outline' }}</v-icon>
                    {{ showEditor ? 'Cancelar' : 'Agregar comentario' }}
                  </button>
                </div>
              </div>
              <div class="interactions-header-actions">
                <div class="search-wrap">
                  <v-icon size="15" color="#999">mdi-magnify</v-icon>
                  <input class="search-field" placeholder="Buscar…" v-model="interactionSearch" type="text" />
                </div>
              </div>
            </div>

            <div class="interactions-feed" v-if="currentMeeting.interactions && currentMeeting.interactions.length > 0">
              <Interactions
                itemType="meetings_interactions"
                :interactions="filteredInteractions"
                :searchText="interactionSearch"
                :item="currentMeeting"
                @reload="reloadMeeting"
                @reply-interaction="onReplyInteraction"
                @minimizeParent="minimizeMeeting"
              />
              <div class="mobile-add-comment-footer" v-if="$vuetify.breakpoint.mobile && checkInteractPermission(['interact_meetings'])">
                <button class="mobile-add-comment-btn" @click="toggleEditorWithScroll" :disabled="editConsolidationMode">
                  <v-icon size="18" color="#2D4B95">{{ showEditor ? 'mdi-close' : 'mdi-comment-plus-outline' }}</v-icon>
                  {{ showEditor ? 'Cancelar' : 'Agregar comentario' }}
                </button>
              </div>
            </div>
            <div class="empty-interactions" v-else>
              <v-icon size="36" color="#ddd">mdi-chat-outline</v-icon>
              <p>Sin interacciones aún</p>
              <button class="mobile-add-comment-btn" v-if="$vuetify.breakpoint.mobile && checkInteractPermission(['interact_meetings'])" @click="toggleEditorWithScroll" :disabled="editConsolidationMode">
                <v-icon size="18" color="#2D4B95">{{ showEditor ? 'mdi-close' : 'mdi-comment-plus-outline' }}</v-icon>
                {{ showEditor ? 'Cancelar' : 'Agregar comentario' }}
              </button>
            </div>

            <div class="compose-area" v-if="showEditor" ref="composeArea">
              <div>
                <div class="reply-banner" v-if="interactionParent">
                  <div class="d-flex align-center gap-2">
                    <v-icon size="14" color="#2D4B95">mdi-reply</v-icon>
                    <div>
                      <p class="mb-0 caption font-weight-bold" style="color:#2D4B95">Respondiendo a {{ interactionParent.user ? interactionParent.user.name : '' }}</p>
                      <div v-html="interactionParent.message" class="reply-preview"></div>
                    </div>
                  </div>
                  <button class="icon-action-btn" @click="clearReply">
                    <v-icon size="13">mdi-close</v-icon>
                  </button>
                </div>

                <InteractionEditor
                  v-model="message"
                  item_type="meeting"
                  :item="currentMeeting"
                  @paste-image="onPasteImage"
                  @mentions-updated="onMentionsUpdated" />

                <div v-if="filesPreview.length > 0" class="attachments-preview">
                  <button class="attach-toggle" @click="showAttachments = !showAttachments">
                    <span class="attach-dot"></span>
                    {{ filesPreview.length }} {{ filesPreview.length === 1 ? 'adjunto' : 'adjuntos' }}
                    <v-icon size="14" :class="['chevron', { open: showAttachments }]">mdi-chevron-down</v-icon>
                  </button>
                  <div v-if="showAttachments" class="attach-grid">
                    <div class="img-chip" v-for="(f, i) in filesPreview.filter(f => f.src)" :key="'img-'+i">
                      <img :src="f.src" class="img-thumb" />
                      <span v-if="f.source === 'library'" class="attachment-reference-tag">Biblioteca</span>
                      <button class="chip-remove" @click="removeAttachment(f, i)"><v-icon x-small color="white">mdi-close</v-icon></button>
                    </div>
                    <div class="file-chip" v-for="(f, i) in filesPreview.filter(f => !f.src)" :key="'file-'+i">
                      <v-icon small color="#2D4B95">mdi-file-outline</v-icon>
                      <span class="file-name">{{ f.name }}</span>
                      <span v-if="f.source === 'library'" class="attachment-reference-tag">Biblioteca</span>
                      <button class="chip-remove file" @click="removeAttachment(f, i)"><v-icon x-small>mdi-close</v-icon></button>
                    </div>
                  </div>
                </div>

                <div class="compose-actions mt-1">
                  <button class="send-btn" :disabled="loading" @click="preAddInteraction()">
                    <v-progress-circular v-if="loading" :size="14" :width="2" indeterminate color="white" />
                    <v-icon v-else size="15" color="white">mdi-send</v-icon>
                    {{ confirmButtonText }}
                  </button>
                  <div class="compose-secondary">
                    <AttachmentSourceButton
                      @files-selected="onFilesChanged"
                      @library-file-selected="onLibraryFileSelected"
                    />
                    <button class="text-action-btn" @click="showEditor = false; clearEditorContent()">
                      <v-icon size="15" color="#666">mdi-close</v-icon>
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <div v-if="isResizing" class="resize-overlay"></div>

    <SignaturePadModal :visible="showSignaturePad" @save="onSignatureSaved" @close="showSignaturePad = false" />
    <PinEntryModal :visible="showPinModal" @submit="onPinSubmit" @resend="handleSignMeeting" @close="showPinModal = false" />
    <AcceptCancelModal :acceptRejectModal="acceptRejectModal" :acceptRejectModalData="acceptRejectModalData" :closeModal="closeModalAcceptCancel" />
    <GlobalFileViewer
      :media-items="viewerMediaItems"
      :media-index="viewerMediaIndex"
      :document-item="viewerDocumentItem"
      :forge-item="viewerForgeItem"
      @close="closeAttachmentViewer"
      @minimize-file="minimizeAttachmentViewer"
      @error="onViewerError"
      @load="onViewerLoad"
    />
  </div>
</template>

<script>
import moment from 'moment'
import _debounce from 'lodash/debounce'
import { v4 as uuidv4 } from 'uuid'
import EventBus from '@/helpers/event_bus'
import AcceptCancelModal from '@/components/Globals/Modals/General/AcceptCancelModal'
import SignaturePadModal from '@/components/Globals/Modals/Signing/SignaturePadModal'
import PinEntryModal from '@/components/Globals/Modals/Signing/PinEntryModal'
import { getUserSignature, requestSignPin, signWithPin } from '@/helpers/api/signing'
import request from '@/helpers/request'
import { update, getMeeting, addInteraction, getPdf, sendEmail } from '@/helpers/api/meetings'
import { getPublicUrl } from '@/helpers/api/tasks'
import { getZippedFiles } from '@/helpers/api/fileUploads'
import { deleteFile } from '@/helpers/api/fileManager'
import Interactions from '@/components/Globals/Helpers/Interactions'
import InteractionEditor from '@/components/Globals/Helpers/InteractionEditor'
import SimpleTimePicker from '@/components/Globals/Helpers/SimpleTimePicker'
import SeenByUsers from '@/components/Globals/Helpers/SeenByUsers'
import GlobalFileViewer from '@/components/Globals/Helpers/GlobalFileViewer'
import AttachmentPreviewButton from '@/components/Globals/Helpers/AttachmentPreviewButton'
import AttachmentSourceButton from '@/components/Globals/Helpers/AttachmentSourceButton'
import ModalStickyHeader from '@/components/Globals/Helpers/ModalStickyHeader'
import { normalizeAttachmentForPreview, toMediaViewerItem } from '@/helpers/attachmentPreview'

const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm'

export default {
  name: 'MeetingModal',
  components: { ModalStickyHeader, AcceptCancelModal, Interactions, InteractionEditor, SimpleTimePicker, SignaturePadModal, PinEntryModal, SeenByUsers, GlobalFileViewer, AttachmentPreviewButton, AttachmentSourceButton },
  computed: {
		currentMeeting() { return this.$store.meetingStore.state.currentMeeting },
    userInfo() { return this.$store.userStore.state.userInfo },
    modality() {
      if (this.editMode) return this._editModality
      return this.currentMeeting.meeting_info && this.currentMeeting.meeting_info.link ? 'virtual' : 'presencial'
    },
    isUserPart() {
      const uid = this.userInfo.id
      const m = this.currentMeeting
      return (m.creator && uid === m.creator.id) ||
        (m.responsible && uid === m.responsible.id) ||
        (m.users_suscriptions && m.users_suscriptions.some(p => p.user_id === uid))
    },
    // Solo el creador o el responsable de la reunión pueden gestionarla:
    // editar datos, agregar/eliminar fotos, gestionar participantes,
    // cambiar responsable y cambiar estado.
    canManageMeeting() {
      const uid = this.userInfo.id
      const m = this.currentMeeting
			const isClosed = m.status === 3
      return !isClosed && ((m.creator && uid === m.creator.id) || (m.responsible && uid === m.responsible.id))
    },
		isResponsible() {
			const uid = this.userInfo.id
			const m = this.currentMeeting
			return (m.responsible && uid === m.responsible.id)
		},
    // Firma del usuario actual, si existe, dentro de currentMeeting.user_signatures
    isUserSigned() {
      return !!this.getParticipantSignature(this.userInfo.id)
    },
    filteredInteractions() {
      const items = this.currentMeeting.interactions
      if (this.interactionSearch && items) {
        return items.filter(i => i.message.toLowerCase().includes(this.interactionSearch.toLowerCase()))
      }
      return items ? [...items] : []
    },
    photoAttachments() {
      return (this.currentMeeting.attachments || []).filter(a => a.pathmedia)
    },
    fileAttachments() {
      return (this.currentMeeting.attachments || []).filter(a => !a.pathmedia)
    },
    meetingParticipants() {
			let join_participants = (this.currentMeeting.users_suscriptions || []).map(s => s.subscriber).filter(Boolean)
			join_participants.push({...this.currentMeeting.creator, cannotBeRemoved: true, title: 'Creador'})
			if (this.currentMeeting.responsible) join_participants.push({...this.currentMeeting.responsible, cannotBeRemoved: true, title: 'Responsable'})
			const uniqueParticipants = []
			const seenIds = new Set()
			for (const participant of join_participants) {
				if (participant && !seenIds.has(participant.id)) {
					seenIds.add(participant.id)
					uniqueParticipants.push(participant)
				}
			}
      return uniqueParticipants
    },
    availableParticipants() {
      const list = this.$store.usersStore.state.usersListForItems?.users || []
      const existingIds = (this.currentMeeting.users_suscriptions || []).map(s => s.user_id)
      return list.filter(u => !existingIds.includes(u.id))
    },
    statusOptions() {
      return [
        { value: 1, label: 'Pendiente', icon: 'mdi-clock-outline', color: '#B45309' },
        { value: 2, label: 'Confirmada', icon: 'mdi-check-circle-outline', color: '#2D4B95' },
        { value: 3, label: 'Finalizada', icon: 'mdi-lock-check-outline', color: '#1B7A4E' }
      ]
    },
    todayDate() { return moment().format('YYYY-MM-DD') },
    minEndDate() { return this.startDatePart || this.todayDate },
    startMinTime() {
      return this.startDatePart === this.todayDate ? moment().format('HH:mm') : null
    },
    endMinTime() {
      if (this.endDatePart && this.startDatePart && this.endDatePart === this.startDatePart) {
        return this.startTimePart || '00:00'
      }
      return null
    }
  },
  watch: {
    message(val) { if (val && val.length > 0) this.handleFormChangeDebounced() },
    files() { this.handleFormChangeDebounced() },
    startDatePart(newVal) { if (newVal) this.startDateMenu = false; this.syncStart() },
    startTimePart() { this.syncStart() },
    endDatePart(newVal) { if (newVal) this.endDateMenu = false; this.syncEnd() },
    endTimePart() { this.syncEnd() }
  },
  data: () => ({
    seenByMenu: false,
    need_reload: false,
    isLoading: false,
    interactionSearch: '',
    searchUserResponsable: '',
    showAttachments: true,
    message: '',
    showEditor: false,
    leftPanelWidth: 50,
    isResizing: false,
    files: [],
    filesPreview: [],
    libraryFiles: [],
    loadingFilePath: false,
    viewerMediaItems: [],
    viewerMediaIndex: null,
    viewerDocumentItem: null,
    viewerForgeItem: null,
    loading: false,
    acceptRejectModal: false,
    acceptRejectModalData: null,
    cancelTimer: 4,
    inCancelTime: false,
    cancelInterval: null,
    confirmButtonText: 'Enviar',
    draftId: null,
    draftCreated: false,
    draftUpdateInterval: 20,
    draftSaveTimer: null,
    handleFormChangeDebounced: null,
    isResetting: false,
    interactionParent: null,
    newInteractionMentions: [],
    // edición
    editMode: false,
    savingEdit: false,
		editConsolidationMode: false,
    editForm: { title: '', description: '', address: '', link: '' },
    _editModality: 'presencial',
    startDateMenu: false,
    endDateMenu: false,
    startDatePart: null,
    startTimePart: null,
    endDatePart: null,
    endTimePart: null,
    endDateError: '',
    // participantes
    addParticipantMenu: false,
    participantToAdd: null,
    // fotos
    settingMainImage: false,
    // firma
    showSignaturePad: false,
    showPinModal: false,
    pendingSignatureBlob: null,
    signatureUploading: false,
    isSigning: false
    ,
    // flag para indicar que el envío actual viene del editor de consolidado
    sendingConsolidation: false
  }),
  mounted() {
    this.setListeners()
    this.handleFormChangeDebounced = _debounce(() => this.checkAndCreateDraft(), 1000)
    window.addEventListener('mousemove', this.handleResize)
    window.addEventListener('mouseup', this.stopResize)
  },
  beforeDestroy() {
    EventBus.$off('completedFileUpload')
    EventBus.$off('onMeetingElementsChange')
    if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
    window.removeEventListener('mousemove', this.handleResize)
    window.removeEventListener('mouseup', this.stopResize)
  },
  methods: {
    statusLabel(status) {
      return (this.statusOptions.find(o => o.value === status) || {}).label || 'Pendiente'
    },
    initials(name) { return (name || '').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') },
    // Busca en currentMeeting.user_signatures la firma registrada para un participante
    getParticipantSignature(userId) {
      const signatures = this.currentMeeting.user_signatures || []
			const found_participant = signatures.find(s => s.user_id === userId && s.user && s.user.signature)
			if (found_participant) return found_participant
			
			if (this.currentMeeting.creator && this.currentMeeting.creator.id === userId && this.currentMeeting.creator.signature) {
				return { user_id: userId, user: { signature: this.currentMeeting.creator.signature } }
			}
			if (this.currentMeeting.responsible && this.currentMeeting.responsible.id === userId && this.currentMeeting.responsible.signature) {
				return { user_id: userId, user: { signature: this.currentMeeting.responsible.signature } }
			}
      return null
    },
    formatDatePart(value) {
      if (!value) return ''
      const m = moment(value, 'YYYY-MM-DD')
      return m.isValid() ? m.format('DD/MM/YYYY') : ''
    },
    toggleEditorWithScroll() {
      // Bloquear apertura del composer si se está editando el consolidado
      if (!this.showEditor && this.editConsolidationMode) {
        if (this.$store && this.$store.globalStore && this.$store.globalStore.dispatch) {
          this.$store.globalStore.dispatch('addMessage', { message: 'Finaliza la edición del consolidado antes de agregar un comentario.', title: 'Atención', created: new Date(), type: 'info', icon: 'mdi-alert-circle', color: '#fb8c00' })
        }
        return
      }

      this.showEditor = !this.showEditor
      if (this.showEditor) {
        // Si abrimos el composer, asegurarnos de salir del modo edición del consolidado
        this.editConsolidationMode = false
        setTimeout(() => {
          if (this.$refs.composeArea) this.$refs.composeArea.scrollIntoView({ behavior: 'smooth', block: 'end' })
        }, 300)
      }
    },

    toggleConsolidationEditor() {
      if (!this.isResponsible) return
      this.editConsolidationMode = !this.editConsolidationMode
      if (this.editConsolidationMode) {
        // Abrir edición de consolidado: cerrar composer y cargar contenido actual
        this.showEditor = false
        this.message = (this.currentMeeting && this.currentMeeting.consolidation && this.currentMeeting.consolidation.message) ? this.currentMeeting.consolidation.message : ''
        this.$nextTick(() => {
          const c = this.$el.querySelector('.consolidation-editor-container')
          if (c) c.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
      } else {
        // Cerrar edición: limpiar editor
        this.clearEditorContent()
      }
    },
    onMentionsUpdated(userIds) { this.newInteractionMentions = userIds },
    onReplyInteraction(interaction) {
      // No permitir responder mientras se edita el consolidado
      if (this.editConsolidationMode) {
        if (this.$store && this.$store.globalStore && this.$store.globalStore.dispatch) {
          this.$store.globalStore.dispatch('addMessage', { message: 'Finaliza la edición del consolidado antes de responder.', title: 'Atención', created: new Date(), type: 'info', icon: 'mdi-alert-circle', color: '#fb8c00' })
        }
        return
      }
      this.interactionParent = interaction
      this.toggleEditorWithScroll()
    },
    clearReply() { this.interactionParent = null },
    checkUsers() {
      if (!this.$store.usersStore.state.loadingUserListForItems &&
        this.$store.usersStore.state.usersListForItems.project_id !== this.currentMeeting.project_id) {
        this.getUserListforItems()
      }
    },
    getUserListforItems() {
      this.$store.usersStore.dispatch('getUsersListForItems', {
        project_id: this.currentMeeting.project_id,
        start: 0, limit: 9999
      })
    },
    checkInteractPermission(permissionToCheck) {
      if (!this.checkPermissions(permissionToCheck)) return false
      return this.checkPermissions(['interact_meetings_others']) || this.isUserPart
    },
    async onPasteImage(file) { await this.createDraft(true); this.onFileChanged(null, file) },
    setListeners() {
      EventBus.$on('completedFileUpload', async (payload) => {
        if (payload && payload.draft_id === this.draftId) {
          const idx = this.filesPreview.findIndex(f => f.uuid === payload.uuid)
          if (idx !== -1) this.$set(this.filesPreview[idx], 'id', payload.file.id)
        } else if (payload && payload.meeting_id && this.currentMeeting && payload.meeting_id == this.currentMeeting.id) {
          this.reloadMeeting()
        }
      })
			EventBus.$on('onMeetingElementsChange', async (payload) => {
				if (payload && payload.meeting_id && this.currentMeeting && payload.meeting_id == this.currentMeeting.id) {
					this.$store.globalStore.dispatch('addMessage', { 
						message: 'Nueva interacción o actualización del consolidado de esta minuta.',
						title: 'Actualización de minuta',
						created: new Date(),
						type: 'success',
						icon: 'mdi-check-circle',
						color: '#3EBFA3'
					})
					this.reloadMeeting()
				}
			})
    },
    handleFormChange() { if (!this.isResetting && this.handleFormChangeDebounced) this.handleFormChangeDebounced() },
    checkAndCreateDraft() { if (!this.draftCreated && !this.draftId) this.createDraft() },
    async createDraft(force_create = false) {
      if (this.draftCreated || this.draftId) return null
      if ((!this.message || this.message.trim() === '') && !force_create) return null
      this.draftCreated = true
      // Nota: se asume un tipo de borrador dedicado a interacciones de reunión
      const draftData = {
        type: 14,
        project_id: this.$store.projectsStore.state.selectedProject.id,
        type_id: this.currentMeeting.id,
        data: JSON.stringify({ message: this.message, title: this.currentMeeting.title, number: this.currentMeeting.number, library_file_ids: this.libraryFiles.map(file => file.id), library_files: this.libraryFiles.map(file => this.serializeLibraryFile(file)) })
      }
      try {
        const { create: createDraftAPI } = await import('@/helpers/api/drafts')
        const response = await createDraftAPI(draftData)
        if (response.code === 200) {
          this.draftId = response.draft.id
          this.startDraftUpdateTimer()
          EventBus.$emit('reloadDraftList')
          return this.draftId
        }
        return null
      } catch (error) { this.draftCreated = false; throw error }
    },
    async exportPdf() {
      try {
        this.loading = true
        const res = await getPdf({ meeting_id: this.currentMeeting.id })
        if (res && res.url) {
          window.open(res.url, '_blank')
        }
      } catch (e) {
        console.error('PDF export error', e)
        this.$toast && this.$toast.error && this.$toast.error('Error al generar el PDF')
      } finally {
        this.loading = false
      }
    },
		async sendMeetingEmail(){
			try {
				this.loading = true
				const res = await sendEmail({ meeting_id: this.currentMeeting.id })
				if (res && res.code === 200) {
					this.$store.globalStore.dispatch('addMessage', { 
						message: 'Se ha enviado el correo a los participantes.',
						title: 'Correo enviado correctamente',
						created: new Date(),
						type: 'success',
						icon: 'mdi-check-circle',
						color: '#3EBFA3'
					})
				} else {
					this.$store.globalStore.dispatch('addMessage', { 
						message: 'Error al enviar el correo, por favor intenta nuevamente.',
						title: 'Error',
						created: new Date(),
						type: 'error',
						icon: 'mdi-alert-circle',
						color: '#fb8c00'
					})
				}
			} catch (e) {
				console.error('Send email error', e)
				this.$store.globalStore.dispatch('addMessage', { 
					message: 'Error al enviar el correo, por favor intenta nuevamente.',
					title: 'Error',
					created: new Date(),
					type: 'error',
					icon: 'mdi-alert-circle',
					color: '#fb8c00'
				})
			} finally {
				this.loading = false
			}
		},
    startDraftUpdateTimer() {
      if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
      this.draftSaveTimer = setInterval(() => this.updateDraft(), this.draftUpdateInterval * 1000)
    },
    async updateDraft() {
      if (!this.draftId) return
      const { update: updateDraftAPI } = await import('@/helpers/api/drafts')
      await updateDraftAPI({
        id: this.draftId, type: 14,
        project_id: this.$store.projectsStore.state.selectedProject.id,
        type_id: this.currentMeeting.id,
        data: JSON.stringify({ message: this.message, title: this.currentMeeting.title, number: this.currentMeeting.number, library_file_ids: this.libraryFiles.map(file => file.id), library_files: this.libraryFiles.map(file => this.serializeLibraryFile(file)) })
      })
    },
    closeModal() {
      const currentMeeting = this.currentMeeting;
      if (!currentMeeting) return;
      const reload = this.need_reload || !currentMeeting.prev_seen_by_me;
      this.updateDraft()
      if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
      this.$store.meetingStore.commit('setCurrentMeeting', null)
      if (reload) {
        this.getMeetingsList()
        EventBus.$emit('reloadMeetings')
      }
      this.loading = false;
      this.files = []; this.filesPreview = []
      this.draftId = null; this.draftCreated = false; this.message = ''
      this.showEditor = false; this.editMode = false
      this.searchUserResponsable = ''
      EventBus.$emit('reloadDraftList')
    },
    async onFilesChanged(e) {
			await this.createDraft();
      const files = Array.isArray(e) ? e : (e?.target?.files || [])
      for (let i = 0; i < files.length; i++) {
        await this.onFileChanged(null, files[i])
      }
    },
    async onFileChanged(e, file = null) {
      const newFile = e?.target?.files[0] || file
      if (!newFile) return
      if (!this.draftId && !this.draftCreated) await this.createDraft()
      const uuid = uuidv4()
      if (newFile.type.includes('image')) {
        const b64 = await this.toBase64(newFile)
        this.filesPreview.push({ src: b64, name: newFile.name, uuid })
      } else {
        this.filesPreview.push({ noImage: true, name: newFile.name, uuid })
      }
      this.$store.globalStore.commit('setFilesToUploadCenter', {
        type: 13,
        payload: { draft_id: this.draftId, project_id: this.$store.projectsStore.state.selectedProject.id, uuid },
        description: this.currentMeeting.title, total: newFile.size, uuid, file: newFile
      })
    },
    removeAttachment(file) {
      const previewIndex = this.filesPreview.indexOf(file)
      if (previewIndex !== -1) this.filesPreview.splice(previewIndex, 1)
      if (file.source === 'library') {
        const libraryIndex = this.libraryFiles.findIndex(item => Number(item.id) === Number(file.id))
        if (libraryIndex !== -1) this.libraryFiles.splice(libraryIndex, 1)
        this.updateDraft()
        return
      }
      if (file.id) deleteFile({ file_id: file.id })
    },
    async onLibraryFileSelected(file) {
      if (!file || !file.id || this.libraryFiles.some(item => Number(item.id) === Number(file.id))) return
      this.libraryFiles.push(file)
      this.filesPreview.push(file.pathmedia?.path
        ? { src: file.pathmedia.path, id: file.id, name: file.name, source: 'library' }
        : { noImage: true, id: file.id, name: file.name, source: 'library' })
      await this.createDraft(true)
      await this.updateDraft()
    },
    serializeLibraryFile(file) {
      return { id: file.id, name: file.name, ext: file.ext, mime_type: file.mime_type, pathmedia: file.pathmedia || null }
    },
    normalizeAttachment(attachment) {
      return normalizeAttachmentForPreview(attachment)
    },
    previewAttachment(attachment) {
      const normalized = normalizeAttachmentForPreview(attachment)
      this.closeAttachmentViewer()

      if (normalized?.pathmedia?.path) {
        const mediaItem = toMediaViewerItem(normalized)
        if (mediaItem) {
          this.viewerMediaItems = [mediaItem]
          this.viewerMediaIndex = 0
          return
        }
      }

      if (normalized?.pathdoc?.path) {
        this.viewerDocumentItem = normalized
        return
      }

      if (normalized?.file_type === 'forge' || normalized?.forge_file?.translated) {
        this.viewerForgeItem = normalized
        return
      }

      this.showErrorMessage('Este archivo no tiene un visor disponible', 'Vista previa no disponible')
    },
    closeAttachmentViewer() {
      this.viewerMediaItems = []
      this.viewerMediaIndex = null
      this.viewerDocumentItem = null
      this.viewerForgeItem = null
    },
    minimizeAttachmentViewer(item) {
      if (!item) return
      this.$store.globalStore.commit('addFileToFileManagerHandler', item)
      this.closeAttachmentViewer()
    },
    onViewerError() {
      this.showErrorMessage('No fue posible cargar el archivo', 'Error de preview')
    },
    onViewerLoad() {},
    toBase64(file) {
      return new Promise(resolve => {
        const r = new FileReader(); r.readAsDataURL(file)
        r.onload = () => resolve(r.result)
        r.onerror = () => resolve(null)
      })
    },
    async getFilesUrl(attachments, singleFile = false, download = false) {
      if (singleFile) attachments = [attachments]
      this.loadingFilePath = true
      const files = await getPublicUrl({ attachments, force_download: download })
      if (files.code === 200) files.files.forEach(f => window.open(f.url))
      this.loadingFilePath = false
    },
    async downloadAttachments(attachments) {
      this.loadingFilePath = true
      const files = await getZippedFiles({ files_to_zip: attachments })
      if (files.code == 200) window.open(files.fileurl)
      this.loadingFilePath = false
    },
    async preAddInteraction(isConsolidation = false) {
      if (this.inCancelTime) {
        clearInterval(this.cancelInterval)
        this.confirmButtonText = 'Enviar'
        this.inCancelTime = false
        // limpiar posible bandera de consolidado si se canceló
        this.sendingConsolidation = false
        return null
      }
      if (!this.message || this.message === '') {
        this.$store.globalStore.dispatch('addMessage', {
          message: 'Por favor escribe un mensaje.', title: 'Error', created: new Date(),
          type: 'success', icon: 'mdi-alert-circle', color: '#fb8c00'
        })
        // asegurar que no quede marcada una intención de consolidado
        this.sendingConsolidation = false
        return false
      }
      // marcar si este envío es un consolidado
      this.sendingConsolidation = !!isConsolidation
      this.inCancelTime = true
      this.cancelTimer = 4
      this.confirmButtonText = 'Cancelar (' + this.cancelTimer + ')'
      this.cancelInterval = setInterval(() => {
        this.cancelTimer--
        this.confirmButtonText = 'Cancelar (' + this.cancelTimer + ')'
        if (this.cancelTimer == 0) {
          this.inCancelTime = false
          this.confirmButtonText = 'Enviar'
          clearInterval(this.cancelInterval)
          this.addInteractionHandler()
        }
      }, 1000)
    },
    async addInteractionHandler() {
      this.loading = true
      const biggerFiles = []
      const formData = new FormData()
      formData.append('meeting_id', this.currentMeeting.id)
      formData.append('message', this.message)
      // Si el envío corresponde al consolidado, marcarlo para el backend
      if (this.sendingConsolidation) formData.append('is_response', 'true')
      if (this.draftId) formData.append('draft_id', this.draftId)
      if (this.interactionParent) formData.append('parent_id', this.interactionParent.id)
      this.newInteractionMentions.forEach(m => formData.append('mentions[]', m))
      this.libraryFiles.forEach(file => formData.append('library_file_ids[]', file.id))
      this.files.forEach(file => {
        if (file && file.size < 1000) formData.append('files[]', file)
        else if (file) biggerFiles.push({
          type: 2,
          payload: { meeting_id: this.currentMeeting.id, project_id: this.$store.projectsStore.state.selectedProject.id },
          description: this.currentMeeting.title,
          total: file.size, uuid: uuidv4(), file
        })
      })
      const interaction = await addInteraction(formData)
      if (interaction.code === 200) {
        if (biggerFiles.length > 0) biggerFiles.forEach(f => {
          f.payload = { ...f.payload, tr_interaction_id: interaction.interaction, project_id: this.$store.projectsStore.state.selectedProject.id }
          this.$store.globalStore.commit('setFilesToUploadCenter', f)
        })
        this.message = ''; this.files = []; this.filesPreview = []; this.libraryFiles = []
        this.draftId = null; this.draftCreated = false; this.interactionParent = null
        // Si fue consolidado, cerrar el modo de edición del consolidado
        if (this.sendingConsolidation) this.editConsolidationMode = false
        this.showEditor = false
        // limpiar bandera de consolidado
        this.sendingConsolidation = false
        if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
        this.reloadMeeting()
      }
      this.loading = false
    },
    clearEditorContent() {
      this.message = ''
      this.files = []
      this.filesPreview = []
      this.interactionParent = null
    },
    minimizeMeeting() {
      const data = {
        task_id: this.currentMeeting.id,
        title: this.currentMeeting.title,
        type: 'Reunión/Minuta',
        type_id: 12,
        number: this.currentMeeting.number
      }
      this.$store.globalStore.commit('addTaskToTasksHandler', data)
      this.closeModal()
    },
    async getMeetingsList() {
      let project_id = this.$store.projectsStore.state.selectedProject?.id || parseInt(localStorage.getItem('selectedProjectID'))
      if (project_id) this.$store.meetingStore.dispatch('getMyMeetings', { project_id })
    },
    async reloadMeeting() {
      this.isLoading = true
      const meeting = await getMeeting({ meeting_id: this.currentMeeting.id })
      if (meeting.code === 200) this.$store.meetingStore.commit('setCurrentMeeting', meeting.meeting)
      else if (meeting.code === 403) this.$store.meetingStore.commit('setCurrentMeeting', null)
      this.isLoading = false
    },
    async saveMeetingUpdate(partial) {
      const m = this.currentMeeting
      const payload = {
        meeting_id: m.id,
        title: partial.title ?? m.title,
        description: partial.description ?? m.description,
        start: partial.start ?? m.start,
        end: partial.end ?? m.end,
        responsible_id: partial.responsible_id ?? m.responsible_id,
        address: partial.address ?? (m.meeting_info && m.meeting_info.address),
        link: partial.link ?? (m.meeting_info && m.meeting_info.link),
        main_image_id: 'main_image_id' in partial ? partial.main_image_id : (m.meeting_info && m.meeting_info.main_image_id),
        status: partial.status ?? m.status,
        participants: partial.participants ?? (m.users_suscriptions || []).map(s => s.user_id)
      }
      const res = await update(payload)
      if (res.code === 200) this.reloadMeeting()
      return res
    },
    async changeStatus(status) {
      if (!this.canManageMeeting) return
      if (this.currentMeeting.status === status) return
      this.isLoading = true
      await this.saveMeetingUpdate({ status })
      this.isLoading = false
    },
    async changeResponsible(userId) {
      if (!this.canManageMeeting) return
      this.isLoading = true
      await this.saveMeetingUpdate({ responsible_id: userId })
      this.isLoading = false
    },
    addParticipant(userId) {
      if (!this.canManageMeeting) return
      if (!userId) return
      const current = (this.currentMeeting.users_suscriptions || []).map(s => s.user_id)
      if (current.includes(userId)) { this.participantToAdd = null; return }
      this.saveMeetingUpdate({ participants: [...current, userId] })
      this.participantToAdd = null
      this.addParticipantMenu = false
    },
    removeParticipant(userId) {
      if (!this.canManageMeeting) return
      const current = (this.currentMeeting.users_suscriptions || []).map(s => s.user_id)
      this.saveMeetingUpdate({ participants: current.filter(id => id !== userId) })
    },
    isMainImage(photo) {
      return this.currentMeeting.meeting_info && this.currentMeeting.meeting_info.main_image_id === photo.id
    },
    async setMainImage(photo) {
      if (!this.canManageMeeting) return
      this.settingMainImage = true
      await this.saveMeetingUpdate({ main_image_id: photo.id })
      this.settingMainImage = false
    },
    async deletePhoto(photo) {
      if (!this.canManageMeeting) return
      await deleteFile({ file_id: photo.id })
      if (this.isMainImage(photo)) await this.saveMeetingUpdate({ main_image_id: null })
      else await this.reloadMeeting()
    },
    // ── Firma / PIN flow ──
    async handleSignMeeting() {
      if (!this.currentMeeting || !this.isUserPart) return
      this.isLoading = true
      try {
        // Check if user already has a signature
        await getUserSignature()
        // if exists, request PIN
        const pinRes = await requestSignPin({ signable_type: 'meeting', signable_id: this.currentMeeting.id })
        if (pinRes && pinRes.code === 200) {
          this.$store.globalStore.dispatch('addMessage', { message: 'Se ha enviado un PIN a su correo.', title: 'Información', created: new Date(), type: 'success', icon: 'mdi-email', color: '#2D4B95' })
          this.showPinModal = true
        } else {
          this.$store.globalStore.dispatch('addMessage', { message: pinRes?.message || 'Error enviando PIN.', title: 'Error', created: new Date(), type: 'error', icon: 'mdi-alert' })
        }
      } catch (err) {
        // If no signature (404), open signature pad; otherwise show error
        if (err && err.status === 404) {
          this.showSignaturePad = true
        } else {
          this.$store.globalStore.dispatch('addMessage', { message: 'Error al iniciar el proceso de firma.', title: 'Error', created: new Date(), type: 'error', icon: 'mdi-alert' })
        }
      } finally {
        this.isLoading = false
      }
    },

    async onSignatureSaved(blob) {
      // Cierra modal y sube la firma
      this.showSignaturePad = false
      if (!blob) return
      await this.uploadSignature(blob)
    },

    async uploadSignature(blob) {
      this.signatureUploading = true
      try {
        const fd = new FormData()
        fd.append('signature', blob, 'signature.png')
        const res = await request({ url: '/user/setSimpleSignature', method: 'post', data: fd })
        if (res && res.code === 200) {
          this.$store.globalStore.dispatch('addMessage', { message: 'Firma guardada correctamente. Se enviará un PIN para completar la firma.', title: 'Información', created: new Date(), type: 'success', icon: 'mdi-check', color: '#2D4B95' })
          // request PIN and open modal
          const pinRes = await requestSignPin({ signable_type: 'meeting', signable_id: this.currentMeeting.id })
          if (pinRes && pinRes.code === 200) this.showPinModal = true
          else this.$store.globalStore.dispatch('addMessage', { message: pinRes?.message || 'Error enviando PIN.', title: 'Error', created: new Date(), type: 'error', icon: 'mdi-alert' })
        } else {
          this.$store.globalStore.dispatch('addMessage', { message: res?.message || 'Error guardando la firma.', title: 'Error', created: new Date(), type: 'error', icon: 'mdi-alert' })
        }
      } catch (e) {
        this.$store.globalStore.dispatch('addMessage', { message: 'Error al subir la firma.', title: 'Error', created: new Date(), type: 'error', icon: 'mdi-alert' })
      } finally {
        this.signatureUploading = false
      }
    },

    async onPinSubmit(pin) {
      this.showPinModal = false
      if (!pin) return
      this.isSigning = true
      try {
        const res = await signWithPin({ signable_type: 'meeting', signable_id: this.currentMeeting.id, pin })
        if (res && res.code === 200) {
          this.$store.globalStore.dispatch('addMessage', { message: 'Firma registrada correctamente.', title: 'Éxito', created: new Date(), type: 'success', icon: 'mdi-check', color: '#10B981' })
          await this.reloadMeeting()
        } else {
          this.$store.globalStore.dispatch('addMessage', { message: res?.message || 'PIN inválido o expirado.', title: 'Error', created: new Date(), type: 'error', icon: 'mdi-alert' })
        }
      } catch (e) {
        this.$store.globalStore.dispatch('addMessage', { message: 'Error al procesar la firma.', title: 'Error', created: new Date(), type: 'error', icon: 'mdi-alert' })
      } finally {
        this.isSigning = false
      }
    },
    onPhotoButtonClick() { this.$refs.photoUploader.click() },
    async onPhotoFileChanged(e) {
      if (!this.canManageMeeting) return
      const newFile = e?.target?.files[0]
      if (!newFile) return
      const uuid = uuidv4()
      this.$store.globalStore.commit('setFilesToUploadCenter', {
        type: 18,
        payload: { meeting_id: this.currentMeeting.id, project_id: this.$store.projectsStore.state.selectedProject.id, uuid },
        description: this.currentMeeting.title, total: newFile.size, uuid, file: newFile
      })
      e.target.value = ''
    },
    // ── Edición de la reunión ──
    startEdit() {
      if (!this.canManageMeeting) return
      this.editMode = true
      this.editForm.title = this.currentMeeting.title
      this.editForm.description = this.currentMeeting.description
      this.editForm.address = (this.currentMeeting.meeting_info && this.currentMeeting.meeting_info.address) || ''
      this.editForm.link = (this.currentMeeting.meeting_info && this.currentMeeting.meeting_info.link) || ''
      this._editModality = this.currentMeeting.meeting_info && this.currentMeeting.meeting_info.link ? 'virtual' : 'presencial'
      const startMoment = moment(this.currentMeeting.start, DATETIME_FORMAT)
      if (startMoment.isValid()) { this.startDatePart = startMoment.format('YYYY-MM-DD'); this.startTimePart = startMoment.format('HH:mm') }
      if (this.currentMeeting.end) {
        const endMoment = moment(this.currentMeeting.end, DATETIME_FORMAT)
        if (endMoment.isValid()) { this.endDatePart = endMoment.format('YYYY-MM-DD'); this.endTimePart = endMoment.format('HH:mm') }
      }
    },
    cancelEdit() {
      this.editMode = false
      this.endDateError = ''
      this.startDatePart = null; this.startTimePart = null; this.endDatePart = null; this.endTimePart = null
    },
    syncStart() {
      if (this.startDatePart && this.startTimePart) {
        const combined = moment(`${this.startDatePart} ${this.startTimePart}`, DATETIME_FORMAT)
        if (combined.isValid()) this._pendingStart = combined.format(DATETIME_FORMAT)
      }
      this.validateEndAfterStart()
    },
    syncEnd() {
      if (this.endDatePart && this.endTimePart) {
        const combined = moment(`${this.endDatePart} ${this.endTimePart}`, DATETIME_FORMAT)
        if (this._pendingStart && combined.isBefore(moment(this._pendingStart, DATETIME_FORMAT))) {
          this.endDateError = 'La fecha de término no puede ser anterior a la de inicio'
          this.endTimePart = null
          return
        }
        this.endDateError = ''
        this._pendingEnd = combined.format(DATETIME_FORMAT)
      } else {
        this._pendingEnd = null
      }
    },
    validateEndAfterStart() {
      if (!this._pendingEnd || !this._pendingStart) { this.endDateError = ''; return }
      const start = moment(this._pendingStart, DATETIME_FORMAT)
      const end = moment(this._pendingEnd, DATETIME_FORMAT)
      if (end.isBefore(start)) {
        this.endDateError = 'La fecha de término no puede ser anterior a la de inicio'
        this._pendingEnd = null
        this.endDatePart = null
        this.endTimePart = null
      } else {
        this.endDateError = ''
      }
    },
    async saveEdit() {
      if (!this.canManageMeeting) return
      if (this.endDateError) return
      this.savingEdit = true
      await this.saveMeetingUpdate({
        title: this.editForm.title,
        description: this.editForm.description,
        start: this._pendingStart || this.currentMeeting.start,
        end: this._pendingEnd || null,
        address: this._editModality === 'presencial' ? this.editForm.address : null,
        link: this._editModality === 'virtual' ? this.editForm.link : null
      })
      this.savingEdit = false
      this.editMode = false
    },
    closeModalAcceptCancel() {
      this.acceptRejectModal = false
      this.acceptRejectModalData = null
    },
    startResize(e) { this.isResizing = true; e.preventDefault() },
    handleResize(e) {
      if (!this.isResizing) return
      const dialog = document.querySelector('.meeting-dialog')
      if (!dialog) return
      const modalBody = dialog.querySelector('.modal-body')
      if (!modalBody) return
      const rect = modalBody.getBoundingClientRect()
      const newLeftWidth = ((e.clientX - rect.left) / rect.width) * 100
      if (newLeftWidth >= 25 && newLeftWidth <= 75) this.leftPanelWidth = newLeftWidth
    },
    stopResize() { if (this.isResizing) this.isResizing = false }
  }
}
</script>

<style scoped>
.attachment-reference-tag {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 1px 5px;
  border-radius: 4px;
  background: #eef2ff;
  color: #2d4b95;
  font-size: 10px;
  line-height: 14px;
  white-space: nowrap;
}

/* ─── TOKENS ─────────────────────────────────────────── */
:root {
  --blue: #2D4B95;
  --blue-sec: #5E85EF;
  --blue-light: #EEF2FF;
  --text: #1A1D23;
  --text-muted: #6B7280;
  --border: #E5E8EF;
  --surface: #F8F9FC;
  --radius: 12px;
  --radius-sm: 8px;
}

/* ─── DIALOG ─────────────────────────────────────────── */
.meeting-modal-wrapper::v-deep .meeting-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}
.modal-card {
  border-radius: 16px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

/* ─── TOP BAR ────────────────────────────────────────── */
.topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 24px 16px;
  background: #F4F6FB;
  border-bottom: 1px solid #E0E5F0;
  flex-shrink: 0;
}
.topbar-left { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; }
.topbar-icon {
  width: 36px; height: 36px; border-radius: 10px;
  background: #E8EEFA; border: 1px solid #C8D4F0;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.topbar-titles { flex: 1; min-width: 0; }
.topbar-title {
  font-size: 16px; font-weight: 600; color: #1A1D23;
  margin: 0; line-height: 1.4;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.topbar-number { font-weight: 400; color: #2D4B95; font-size: 14px; }
.topbar-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.title-edit-input { max-width: 320px; }

/* ─── STATUS STRIP ───────────────────────────────────── */
.status-strip {
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; padding: 10px 24px;
  background: #FAFBFF; border-bottom: 1px solid #E5E8EF;
  flex-wrap: wrap; flex-shrink: 0;
}
.status-strip-pills { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; flex: 1; }
.status-strip-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; margin-left: auto; }
.pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 10px; border-radius: 999px;
  font-size: 11px; font-weight: 500; line-height: 1.5;
}
.pill--blue    { background: #E8EEFA; color: #2D4B95; }
.pill--indigo  { background: #EEF2FF; color: #4338CA; }
.pill--modality-presencial { background: #FEF3C7; color: #B45309; }
.pill--modality-virtual    { background: #DBEAFE; color: #1D4ED8; }
.pill--status-1 { background: #FEF3C7; color: #B45309; }
.pill--status-2 { background: #E8EEFA; color: #2D4B95; }
.pill--status-3 { background: #DCFCE7; color: #15803D; }
.status-change-btn {
  display: inline-flex; align-items: center; gap: 5px;
  background: white; border: 1px solid #E5E8EF; cursor: pointer;
  border-radius: 7px; padding: 6px 12px; font-size: 12px; font-weight: 600; color: #2D4B95;
  transition: background 0.12s;
}
.status-change-btn:hover { background: #EEF2FF; }
.signed-indicator {
  display: inline-flex; align-items: center; gap: 5px;
  background: #DCFCE7; border: 1px solid #BBF7D0;
  border-radius: 7px; padding: 6px 12px; font-size: 12px; font-weight: 600; color: #1B7A4E;
}

/* ─── BODY ───────────────────────────────────────────── */
.modal-body {
  display: flex; flex: 1;
  overflow: hidden; min-height: 0;
}
.modal-body.resizing { cursor: col-resize !important; user-select: none !important; }

/* LEFT panel */
.panel-detail {
  min-width: 0; border-right: none;
  display: flex; flex-direction: column;
  overflow: hidden; flex-shrink: 0;
}
.panel-scroll {
  flex: 1; overflow-y: auto; padding: 20px 24px;
}
.panel-scroll::-webkit-scrollbar { width: 4px; }
.panel-scroll::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 4px; }

/* RESIZER */
.panel-resizer {
  width: 12px; min-width: 12px; background: transparent;
  cursor: col-resize; flex-shrink: 0; position: relative;
  user-select: none; z-index: 100;
  display: flex; align-items: center; justify-content: center;
}
.panel-resizer::before {
  content: ''; position: absolute; top: 0; left: 5px;
  width: 2px; height: 100%; background: #E5E8EF; transition: background 0.2s;
}
.panel-resizer:hover::before { background: #2D4B95; }
.resize-handle {
  position: absolute; top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 4px; height: 40px; background: white; border-radius: 2px;
  opacity: 0; transition: opacity 0.2s; pointer-events: none;
  box-shadow: 0 0 4px rgba(0,0,0,0.3);
}
.panel-resizer:hover .resize-handle { opacity: 1; }
.resize-overlay {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  z-index: 9999; cursor: col-resize; user-select: none; background: transparent;
}

/* RIGHT panel */
.panel-interactions {
  display: flex; flex-direction: column;
  overflow: hidden; background: #FAFBFF; flex: 1 1 0; min-width: 0;
}
.panel-interactions--editor-open { overflow-y: auto; }
.panel-interactions--editor-open .interactions-feed { flex: none; overflow-y: visible; }
.panel-interactions--editor-open .compose-area { max-height: none; overflow-y: visible; }
.interactions-header {
  padding: 14px 18px; border-bottom: 1px solid #E5E8EF;
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; flex-shrink: 0;
}
.interactions-header-actions { display: flex; align-items: center; gap: 8px; }
.interactions-feed { flex: 1; overflow-y: auto; padding: 12px 18px; }
.interactions-feed::-webkit-scrollbar { width: 4px; }
.interactions-feed::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 4px; }
.empty-interactions {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 8px; color: #9CA3AF; font-size: 13px;
}
.mobile-add-comment-footer { display: flex; justify-content: center; padding: 12px 0; border-top: 1px solid #E5E8EF; margin-top: 8px; }
.mobile-add-comment-btn {
  display: flex; align-items: center; gap: 8px; padding: 10px 16px;
  background-color: #F0F4FF; border: 1px solid #D0DEFF; border-radius: 8px;
  color: #2D4B95; font-size: 13px; font-weight: 500; cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
}
.mobile-add-comment-btn:hover { background-color: #D0DEFF; border-color: #2D4B95; }
.mobile-add-comment-btn:active { background-color: #B8CCFF; }

/* ─── META SECTION ───────────────────────────────────── */
.meta-section { margin-bottom: 20px; }
.meta-two-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.meta-left-column, .meta-right-column { display: flex; flex-direction: column; gap: 12px; }
.meta-item { display: flex; flex-direction: column; gap: 2px; }
.meta-label { font-size: 11px; font-weight: 600; color: #6B7280; text-transform: uppercase; letter-spacing: 0.04em; }
.meta-value { font-size: 13px; color: #1A1D23; font-weight: 500; }
.meta-link { color: #2D4B95; text-decoration: underline; word-break: break-all; }
.meta-responsible { display: flex; align-items: center; gap: 6px; }
.meta-divider { margin: 8px 0 !important; border-color: #E5E8EF !important; }

/* ─── EDIT BLOCK ──────────────────────────────────────── */
.edit-block { margin-top: 4px; }
.fields-grid { display: grid; gap: 12px; }
.fields-grid.four-col { grid-template-columns: 1fr 1fr 1fr 1fr; }
.field-group { display: flex; flex-direction: column; }
@media (max-width: 900px) { .fields-grid.four-col { grid-template-columns: 1fr 1fr; } }
.field-error { margin: 8px 0 0; font-size: 11px; color: #F4516C; font-weight: 500; }
.modality-group { display: flex; gap: 6px; }
.modality-btn {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 7px; border: 1.5px solid #E5E7EB;
  background: white; cursor: pointer; font-size: 12px; font-weight: 500; color: #374151;
  transition: all 0.15s;
}
.modality-btn:hover { border-color: #5E85EF80; }
.modality-btn.active { background: #5E85EF; border-color: #5E85EF; color: white; }

/* ─── CONTENT SECTIONS ───────────────────────────────── */
.content-section { margin-bottom: 20px; padding-top: 16px; border-top: 1px solid #F3F4F6; }
.section-title { font-size: 13px; font-weight: 600; color: #374151; margin: 0 0 10px; }
.section-header-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.description-body { font-size: 13px; color: #374151; line-height: 1.6; }
::v-deep .description-body p { margin-bottom: 4px; }
.empty-note { font-size: 13px; color: #9CA3AF; margin: 0; }

/* Consolidado editor container to enforce height when TinyMCE autoresize is not enough */
.consolidation-editor-container {
  overflow: auto;
}
/deep/ .custom-html-consolidation-container {
	font-size: 14px;
}
/deep/ .custom-html-consolidation-container p{margin: 0;}

.add-subtask-btn {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border: 1px dashed #D1D5DB; border-radius: 7px;
  background: transparent; font-size: 12px; color: #6B7280;
  cursor: pointer; transition: all 0.15s; flex-shrink: 0;
}
.add-subtask-btn:hover { border-color: #2D4B95; color: #2D4B95; background: #EEF2FF; }

/* ─── PHOTOS ──────────────────────────────────────────── */
.photo-gallery { display: grid; grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); gap: 10px; }
.photo-card { position: relative; border-radius: 10px; overflow: hidden; aspect-ratio: 1/1; border: 2px solid transparent; }
.photo-card--main { border-color: #F59E0B; }
.photo-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.photo-overlay {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.25); opacity: 0; transition: opacity 0.15s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.photo-card:hover .photo-overlay { opacity: 1; }
.photo-btn {
  width: 28px; height: 28px; border-radius: 50%;
  background: rgba(0,0,0,0.5); border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.12s;
}
.photo-btn:hover:not(:disabled) { background: rgba(0,0,0,0.7); }
.photo-btn--danger:hover { background: #F4516C; }
.photo-btn:disabled { cursor: default; }
.photo-main-badge {
  position: absolute; bottom: 4px; left: 4px;
  display: inline-flex; align-items: center; gap: 3px;
  background: #F59E0B; color: white; font-size: 10px; font-weight: 600;
  padding: 2px 6px; border-radius: 10px;
}

/* ─── ATTACHMENTS ────────────────────────────────────── */
.attachments-list { background: #F9FAFB; border-radius: 8px; overflow: hidden; border: 1px solid #E5E8EF; }
.attachment-row { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; font-size: 12px; }
.attachment-row:not(:last-child) { border-bottom: 1px solid #E5E8EF; }
.attachment-info { display: flex; align-items: center; gap: 6px; min-width: 0; }
.attachment-name { color: #374151; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.attachment-actions { display: flex; gap: 4px; flex-shrink: 0; }

/* ─── SEARCH ─────────────────────────────────────────── */
.search-wrap { display: flex; align-items: center; gap: 6px; background: #F3F4F6; border-radius: 8px; padding: 5px 10px; }
.search-field { border: none; background: transparent; outline: none; font-size: 12px; color: #374151; width: 110px; }
.search-field::placeholder { color: #9CA3AF; }

/* ─── MENUS ───────────────────────────────────────────── */
.create-btn {
  display: inline-flex; align-items: center; gap: 5px;
  background: #2D4B95; color: white; border: none; cursor: pointer;
  border-radius: 8px; padding: 6px 12px; font-size: 12px; font-weight: 600;
  transition: background 0.15s;
}
.create-btn:hover { background: #243d7a; }
.create-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.create-menu { background: white; border-radius: 10px; box-shadow: 0 4px 20px rgba(0,0,0,0.12); overflow: hidden; min-width: 200px; }
.create-menu-item {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 10px 14px; border: none; background: transparent;
  font-size: 13px; color: #374151; cursor: pointer; text-align: left; transition: background 0.1s;
}
.create-menu-item:hover { background: #EEF2FF; }
.create-menu-item:disabled { opacity: 0.4; cursor: not-allowed; }

/* ─── COMPOSE AREA ───────────────────────────────────── */
.compose-area {
  border-top: 1px solid #E5E8EF; padding: 12px 16px;
  background: white; flex-shrink: 0; display: flex; flex-direction: column; gap: 8px;
  max-height: 55vh; overflow-y: auto;
}
.add-comment-section { display: flex; align-items: center; padding: 8px 0; }
.add-comment-btn {
  display: inline-flex; align-items: center; gap: 8px;
  color: #2D4B95; cursor: pointer; border-radius: 8px;
  font-size: 13px; font-weight: 600; transition: all 0.15s;
}
.add-comment-btn:hover { background: #EEF2FF; }
.reply-banner {
  display: flex; align-items: flex-start; justify-content: space-between;
  background: #EEF2FF; border-left: 3px solid #2D4B95;
  border-radius: 0 6px 6px 0; padding: 8px 10px;
}
.reply-preview {
  font-size: 11px; color: #9CA3AF; max-height: 18px; overflow: hidden;
  white-space: nowrap; text-overflow: ellipsis; max-width: 260px;
}
.reply-preview * { display: inline; }
.compose-actions { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.send-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: #2D4B95; color: white; border: none; cursor: pointer;
  border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600;
  transition: background 0.15s;
}
.send-btn:hover:not(:disabled) { background: #243d7a; }
.send-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.compose-secondary { display: flex; align-items: center; gap: 6px; }

/* ─── BUTTONS ────────────────────────────────────────── */
.icon-action-btn {
  width: 28px; height: 28px; border-radius: 7px;
  background: #EEF2FF; border: none; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; transition: background 0.12s;
}
.icon-action-btn:hover { background: #dde5f9; }
.text-action-btn {
  display: inline-flex; align-items: center; gap: 5px;
  background: none; border: 1px solid #E5E8EF; cursor: pointer;
  border-radius: 7px; padding: 5px 10px; font-size: 12px; color: #374151; transition: background 0.12s;
}
.text-action-btn:hover { background: #F3F4F6; }

/* ─── ATTACHMENTS PREVIEW (compositor) ───────────────── */
.attachments-preview { display: flex; flex-direction: column; gap: 6px; }
.attach-toggle {
  display: inline-flex; align-items: center; gap: 5px;
  background: #F3F4F6; border: 1px solid #E5E8EF; border-radius: 7px;
  padding: 4px 10px; font-size: 12px; color: #6B7280; cursor: pointer;
}
.attach-dot { width: 6px; height: 6px; border-radius: 50%; background: #2D4B95; flex-shrink: 0; }
.chevron { transition: transform 0.15s; }
.chevron.open { transform: rotate(180deg); }
.attach-grid { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
.img-chip { position: relative; width: 56px; height: 56px; border-radius: 8px; overflow: hidden; border: 1px solid #E5E8EF; flex-shrink: 0; }
.img-thumb { width: 100%; height: 100%; object-fit: cover; }
.chip-remove {
  position: absolute; top: 2px; right: 2px; width: 16px; height: 16px;
  border-radius: 50%; background: rgba(0,0,0,0.5); border: none;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
}
.file-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 8px; border: 1px solid #E5E8EF; border-radius: 7px;
  background: #F9FAFB; font-size: 12px; max-width: 180px;
}
.file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
.chip-remove.file {
  position: static; width: 16px; height: 16px; background: #E5E8EF;
  border-radius: 50%; border: none; display: flex; align-items: center;
  justify-content: center; cursor: pointer; flex-shrink: 0;
}

/* ─── PARTICIPANTS ────────────────────────────────────── */
.participant-list { display: flex; flex-direction: column; gap: 4px; }
.participant-card {
  display: flex; align-items: center; justify-content: space-between;
  padding: 6px 10px; background: #F9FAFB; border: 1px solid #E5E8EF;
  border-radius: 8px; gap: 8px;
}
.participant-left { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; }
.p-avatar {
  width: 28px; height: 28px; border-radius: 50%; background: #E0E7FF;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 600; color: #4F46E5; flex-shrink: 0; overflow: hidden;
}
.p-avatar img { width: 100%; height: 100%; object-fit: cover; }
.p-info { display: flex; flex-direction: column; min-width: 0; }
.p-name { font-size: 12px; font-weight: 500; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.p-title { font-size: 10px; color: #6B7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.participant-right { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.p-remove-btn {
  display: flex; align-items: center; justify-content: center;
  padding: 3px; border: none; background: transparent; cursor: pointer;
  border-radius: 4px; transition: background 0.15s;
}
.p-remove-btn:hover { background: #FEE2E2; }
.signature-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 600;
  white-space: nowrap; cursor: default;
}
.signature-chip--signed { background: #DCFCE7; color: #1B7A4E; cursor: pointer; }
.signature-chip--signed:hover { background: #BBF7D0; }
.signature-chip--pending { background: #FEF3C7; color: #B45309; }
.signature-preview-card { width: 220px; }
.signature-preview-img {
  width: 100%; max-height: 140px; object-fit: contain;
  background: #F9FAFB; border: 1px solid #E5E8EF; border-radius: 6px;
}

/* ─── LOADING ─────────────────────────────────────────── */
.loading-card { min-height: 180px; }
.loading-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px; background: #F4F6FB; border-bottom: 1px solid #E0E5F0;
}
.modal-brand { display: flex; align-items: center; gap: 10px; }
.brand-icon-dark { width: 32px; height: 32px; border-radius: 8px; background: #E8EEFA; border: 1px solid #C8D4F0; display: flex; align-items: center; justify-content: center; }
.brand-label-dark { font-size: 15px; font-weight: 600; color: #1A1D23; }
.topbar-action-btn {
  display: flex; align-items: center; justify-content: center;
  height: 24px !important; padding: 5px !important;
  min-width: 28px !important; aspect-ratio: 1 !important;
  border-radius: 6px; border: none; cursor: pointer; transition: opacity 0.15s;
}
.topbar-action-btn.big { height: 30px !important; min-width: 30px !important; }
.loading-body { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px; gap: 12px; }
.loading-text { font-size: 13px; color: #6B7280; margin: 0; }

/* ─── RESPONSIBLE MENU ───────────────────────────────── */
.responsible-menu { border-radius: 10px !important; overflow: hidden; width: 280px !important; }
.user-avatar-sm { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.search-responsible-wrapper { position: relative; padding: 8px 12px; border-bottom: 1px solid #E5E8EF; }
.search-responsible-wrapper::v-deep .v-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); pointer-events: none; }
.search-responsible-input { width: 100%; border: 1px solid #E5E8EF; border-radius: 6px; padding: 8px 12px 8px 32px; font-size: 12px; outline: none; font-family: inherit; }
.search-responsible-input:focus { border-color: #2D4B95; background-color: #F8F9FC; }
.search-responsible-input::placeholder { color: #999; }

/deep/ .remove-bottom-details .v-text-field__details { display: none; }
/deep/ .remove-bottom-details .v-input__slot { margin-bottom: 0; }

/* ─── MOBILE ──────────────────────────────────────────── */
@media (max-width: 768px) {
  .modal-card { border-radius: 0 !important; max-height: 100vh; height: 100vh; overflow-y: auto; overflow-x: hidden; }
  .topbar { flex-direction: column; align-items: stretch; padding: 12px 16px; gap: 6px; }
  .topbar-right { order: -1; justify-content: flex-end; }
  .topbar-left { order: 0; }
  .modal-body { flex-direction: column; overflow: visible; min-height: unset; }
  .panel-detail { width: 100% !important; flex: none; border-right: none; border-bottom: 1px solid #E5E8EF; overflow: visible; }
  .panel-scroll { overflow: visible; padding: 14px 16px; flex: none; height: auto; }
  .panel-resizer { display: none; }
  .panel-interactions { width: 100% !important; flex: none; background: white; min-height: 400px; overflow: visible; }
  .interactions-feed { overflow: visible; min-height: 200px; padding: 12px 16px; flex: none; }
  .meta-two-columns { grid-template-columns: 1fr 1fr; gap: 12px; }
  .fields-grid.four-col { grid-template-columns: 1fr 1fr; }
  .status-strip { padding: 8px 16px; flex-direction: column; align-items: flex-start; }
  .status-strip-actions { width: 100%; justify-content: flex-end; }
  .interactions-header { flex-wrap: wrap; gap: 8px; padding: 12px 16px; }
  .interactions-header-actions { width: 100%; justify-content: flex-end; }
  .search-field { width: 100%; }
  .search-wrap { flex: 1; }
  .compose-area { padding: 10px 14px; }
}

/deep/ .consolidation-card p { margin: 0; }
.consolidation-card {
  border: 1px solid #e4e4e7;
  border-radius: 10px;
  background: #fafafa;
  overflow: hidden;
  margin-top: 8px;
  width: 100%;
  box-sizing: border-box;
  display: block;
}

.consolidation-card__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f1f1f3;
  border-bottom: 1px solid #e4e4e7;
}

.consolidation-card__avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.consolidation-card__avatar--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #6366f1;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
}

.consolidation-card__meta {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.consolidation-card__label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #71717a;
}

.consolidation-card__author {
  font-size: 12.5px;
  color: #52525b;
}

.consolidation-card__author strong {
  color: #27272a;
  font-weight: 600;
}

.consolidation-card__content {
  padding: 14px 16px;
  font-size: 14px;
  color: #3f3f46;
  line-height: 1.5;
}

.consolidation-card__actions {
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.consolidation-card__content p {
  margin: 0 0 8px;
}

.consolidation-card__content p:last-child {
  margin-bottom: 0;
}

.consolidation-card__content p:empty {
  display: none;
}
</style>